import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export type PeriodType = 'daily' | 'weekly' | 'monthly';

export interface SalesTarget {
  id: string;
  organization_id: string;
  location_id: string;
  target_sales_amount: number;
  target_order_count: number;
  target_avg_ticket: number;
  effective_from: string;
  effective_to: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  location?: {
    id: string;
    name: string;
  };
}

export interface SalesTargetProgress {
  target_id: string | null;
  period_type: string;
  target_sales: number;
  target_orders: number;
  target_avg_ticket: number;
  actual_sales: number;
  actual_orders: number;
  actual_avg_ticket: number;
  sales_progress_pct: number;
  orders_progress_pct: number;
  avg_ticket_progress_pct: number;
  period_start: string;
  period_end: string;
  days_remaining: number;
  sales_to_goal: number;
  orders_to_goal: number;
}

export interface SalesTargetHistory {
  id: string;
  organization_id: string;
  location_id: string;
  snapshot_date: string;
  period_type: string;
  period_start_date: string;
  period_end_date: string;
  target_sales_amount: number;
  target_order_count: number;
  target_avg_ticket: number;
  actual_sales_amount: number;
  actual_order_count: number;
  actual_avg_ticket: number;
  sales_progress_pct: number;
  orders_progress_pct: number;
  avg_ticket_progress_pct: number;
  ptd_actual_sales: number;
  ptd_actual_orders: number;
  ptd_target_sales: number;
  ptd_target_orders: number;
  is_best_day_sales: boolean;
  is_best_day_orders: boolean;
  created_at: string;
}

export interface BestDayStats {
  best_sales_date: string | null;
  best_sales_amount: number | null;
  best_orders_date: string | null;
  best_orders_count: number | null;
}

export interface CreateSalesTargetInput {
  location_id: string;
  target_sales_amount: number;
  target_order_count: number;
  target_avg_ticket: number;
  effective_from?: string;
}

export interface UpdateSalesTargetInput {
  id: string;
  target_sales_amount?: number;
  target_order_count?: number;
  target_avg_ticket?: number;
  effective_to?: string | null;
}

// Fetch all sales targets, optionally filtered by location
export function useSalesTargets(locationId?: string | null) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['sales-targets', profile?.organization_id, locationId],
    queryFn: async () => {
      let query = supabase
        .from('sales_targets')
        .select(`
          *,
          location:locations(id, name)
        `)
        .eq('organization_id', profile!.organization_id)
        .is('effective_to', null) // Only active targets
        .order('created_at', { ascending: false });

      if (locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as SalesTarget[];
    },
    enabled: !!profile?.organization_id,
  });
}

// Get a single active target for a location
export function useSalesTarget(locationId: string | null | undefined) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['sales-target', locationId],
    queryFn: async () => {
      if (!locationId) return null;

      const { data, error } = await supabase
        .from('sales_targets')
        .select(`
          *,
          location:locations(id, name)
        `)
        .eq('location_id', locationId)
        .is('effective_to', null)
        .order('effective_from', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as SalesTarget | null;
    },
    enabled: !!profile?.organization_id && !!locationId,
  });
}

// Get real-time progress against target using the RPC function
export function useSalesTargetProgress(
  locationId: string | null | undefined,
  periodType: PeriodType = 'daily'
) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['sales-target-progress', locationId, periodType],
    queryFn: async () => {
      if (!locationId) return null;

      const { data, error } = await supabase.rpc('get_sales_target_progress', {
        p_location_id: locationId,
        p_period_type: periodType,
      });

      if (error) throw error;

      // RPC returns an array, get first result
      const result = Array.isArray(data) ? data[0] : data;
      return result as SalesTargetProgress | null;
    },
    enabled: !!profile?.organization_id && !!locationId,
    refetchInterval: 60000, // Refetch every minute for live updates
  });
}

// Get progress for all period types at once
export function useSalesTargetProgressAll(locationId: string | null | undefined) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['sales-target-progress-all', locationId],
    queryFn: async () => {
      if (!locationId) return { daily: null, weekly: null, monthly: null };

      const [dailyRes, weeklyRes, monthlyRes] = await Promise.all([
        supabase.rpc('get_sales_target_progress', {
          p_location_id: locationId,
          p_period_type: 'daily',
        }),
        supabase.rpc('get_sales_target_progress', {
          p_location_id: locationId,
          p_period_type: 'weekly',
        }),
        supabase.rpc('get_sales_target_progress', {
          p_location_id: locationId,
          p_period_type: 'monthly',
        }),
      ]);

      return {
        daily: Array.isArray(dailyRes.data) ? dailyRes.data[0] : dailyRes.data,
        weekly: Array.isArray(weeklyRes.data) ? weeklyRes.data[0] : weeklyRes.data,
        monthly: Array.isArray(monthlyRes.data) ? monthlyRes.data[0] : monthlyRes.data,
      } as {
        daily: SalesTargetProgress | null;
        weekly: SalesTargetProgress | null;
        monthly: SalesTargetProgress | null;
      };
    },
    enabled: !!profile?.organization_id && !!locationId,
    refetchInterval: 60000,
  });
}

// Get best day stats for a location
export function useBestDayStats(locationId: string | null | undefined) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['best-day-stats', locationId],
    queryFn: async () => {
      if (!locationId) return null;

      const { data, error } = await supabase.rpc('get_best_day_stats', {
        p_location_id: locationId,
      });

      if (error) throw error;

      const result = Array.isArray(data) ? data[0] : data;
      return result as BestDayStats | null;
    },
    enabled: !!profile?.organization_id && !!locationId,
  });
}

// Fetch historical target data for charts
export function useSalesTargetHistory(
  locationId: string | null | undefined,
  options?: {
    periodType?: PeriodType;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }
) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['sales-target-history', locationId, options],
    queryFn: async () => {
      if (!locationId) return [];

      let query = supabase
        .from('sales_target_history')
        .select('*')
        .eq('location_id', locationId)
        .order('snapshot_date', { ascending: false });

      if (options?.periodType) {
        query = query.eq('period_type', options.periodType);
      }
      if (options?.startDate) {
        query = query.gte('snapshot_date', options.startDate);
      }
      if (options?.endDate) {
        query = query.lte('snapshot_date', options.endDate);
      }
      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as SalesTargetHistory[];
    },
    enabled: !!profile?.organization_id && !!locationId,
  });
}

// Create a new sales target
export function useCreateSalesTarget() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateSalesTargetInput) => {
      // First, archive any existing active target for this location
      const { error: archiveError } = await supabase
        .from('sales_targets')
        .update({ effective_to: new Date().toISOString().split('T')[0] })
        .eq('location_id', input.location_id)
        .is('effective_to', null);

      if (archiveError) throw archiveError;

      // Create the new target
      const { data, error } = await supabase
        .from('sales_targets')
        .insert({
          organization_id: profile!.organization_id,
          location_id: input.location_id,
          target_sales_amount: input.target_sales_amount,
          target_order_count: input.target_order_count,
          target_avg_ticket: input.target_avg_ticket,
          effective_from: input.effective_from || new Date().toISOString().split('T')[0],
          created_by: profile!.id,
        })
        .select(`
          *,
          location:locations(id, name)
        `)
        .single();

      if (error) throw error;
      return data as SalesTarget;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sales-targets'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target', variables.location_id] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-progress'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-progress-all'] });
    },
  });
}

// Update an existing sales target
export function useUpdateSalesTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSalesTargetInput) => {
      const { id, ...updateData } = input;

      const { data, error } = await supabase
        .from('sales_targets')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          location:locations(id, name)
        `)
        .single();

      if (error) throw error;
      return data as SalesTarget;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sales-targets'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target', data.location_id] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-progress'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-progress-all'] });
    },
  });
}

// Delete (archive) a sales target
export function useDeleteSalesTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, locationId }: { id: string; locationId: string }) => {
      // Archive the target instead of deleting
      const { error } = await supabase
        .from('sales_targets')
        .update({ effective_to: new Date().toISOString().split('T')[0] })
        .eq('id', id);

      if (error) throw error;
      return { id, locationId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['sales-targets'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target', result.locationId] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-progress'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-progress-all'] });
    },
  });
}

// Helper to format progress percentage with color
export function getProgressColor(percentage: number): string {
  if (percentage >= 100) return 'text-green-600';
  if (percentage >= 75) return 'text-primary';
  if (percentage >= 50) return 'text-amber-600';
  return 'text-red-600';
}

// Helper to get progress bar color class
export function getProgressBarColor(percentage: number): string {
  if (percentage >= 100) return 'bg-green-500';
  if (percentage >= 75) return 'bg-primary';
  if (percentage >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

// Helper to calculate daily target from monthly
export function calculateDailyTarget(monthlyTarget: number, businessDays: number = 26): number {
  return Math.round((monthlyTarget / businessDays) * 100) / 100;
}

// Helper to calculate weekly target from monthly
export function calculateWeeklyTarget(monthlyTarget: number): number {
  return Math.round((monthlyTarget / 4.33) * 100) / 100;
}
